import React, { useState, useEffect } from 'react';
import { Calculator, DollarSign, Users } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const CostPerWebsiteVisitCalculator = () => {
  const [totalMarketingCost, setTotalMarketingCost] = useState(5000);
  const [totalVisitors, setTotalVisitors] = useState(10000);
  const [costPerVisit, setCostPerVisit] = useState(0);

  useEffect(() => {
    calculateCostPerVisit();
  }, [totalMarketingCost, totalVisitors]);

  const calculateCostPerVisit = () => {
    const cost = totalVisitors > 0 ? totalMarketingCost / totalVisitors : 0;
    setCostPerVisit(parseFloat(cost.toFixed(2)));
  };

  const siteUrl = 'https://www.marketingcalculatorhub.com';
  const pageUrl = `${siteUrl}/cost-per-website-visit-calculator`;
  const logoUrl = `${siteUrl}/logo.png`;

  return (
    <div className="container mx-auto px-4 py-8">
      <Helmet>
        <title>Cost Per Website Visit Calculator: Optimize Traffic Costs | Free Tool</title>
        <meta name="description" content="Calculate your Cost Per Website Visit instantly. Compare with industry benchmarks, get cost optimization tips, and improve marketing efficiency." />
        <meta name="keywords" content="cost per visit calculator, website traffic costs, marketing efficiency metrics, traffic acquisition cost, marketing ROI" />
        <link rel="canonical" href={pageUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content="Free Cost Per Visit Calculator: Optimize Traffic Acquisition" />
        <meta property="og:description" content="Calculate your Cost Per Website Visit instantly. Get actionable insights to reduce traffic costs and improve marketing ROI." />
        <meta property="og:image" content={logoUrl} />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={pageUrl} />
        <meta property="twitter:title" content="Free Cost Per Visit Calculator: Optimize Traffic Acquisition" />
        <meta property="twitter:description" content="Calculate your Cost Per Website Visit instantly. Get actionable insights to reduce traffic costs and improve marketing ROI." />
        <meta property="twitter:image" content={logoUrl} />

        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "Cost Per Website Visit Calculator",
            "description": "Free calculator to measure and optimize website traffic acquisition costs",
            "url": pageUrl,
            "image": logoUrl,
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Any",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "featureList": [
              "Calculate Cost Per Visit",
              "Industry Benchmarks",
              "Cost Optimization Tips",
              "Channel Analysis",
              "Best Practices"
            ],
            "about": {
              "@type": "Thing",
              "name": "Cost Per Website Visit",
              "description": "The average cost to acquire one website visitor through various marketing channels."
            },
            "audience": {
              "@type": "Audience",
              "audienceType": "Digital Marketers, Website Owners, Marketing Managers"
            },
            "mainEntity": {
              "@type": "HowTo",
              "name": "How to Calculate Cost Per Website Visit",
              "step": [
                {
                  "@type": "HowToStep",
                  "text": "Enter total marketing cost"
                },
                {
                  "@type": "HowToStep",
                  "text": "Input total website visitors"
                },
                {
                  "@type": "HowToStep",
                  "text": "View your cost per visit and optimization tips"
                }
              ]
            }
          })}
        </script>
      </Helmet>

      <h1 className="text-3xl font-bold text-gray-800 mb-6 flex items-center">
        <Calculator className="w-8 h-8 mr-2 text-indigo-600" />
        Cost Per Website Visit Calculator
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Input Values</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Total Marketing Cost</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <DollarSign className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={totalMarketingCost}
                  onChange={(e) => setTotalMarketingCost(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="5000"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Total Website Visitors</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Users className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={totalVisitors}
                  onChange={(e) => setTotalVisitors(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="10000"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Results</h2>
          <div className="text-4xl font-bold text-indigo-600 mb-2 flex items-center">
            ${costPerVisit}
            <span className="text-xl text-gray-500 ml-2">per visit</span>
          </div>
          <p className="text-gray-600">Cost Per Website Visit</p>
        </div>
      </div>

      <div className="mt-8 bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Interpretation & Industry Benchmarks</h2>
        <p className="text-gray-600">
          Your Cost Per Website Visit is ${costPerVisit}. This means you're spending an average of ${costPerVisit} to attract each visitor to your website.
        </p>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Channel Benchmarks</h3>
        <ul className="list-disc list-inside text-gray-600 mb-4">
          <li>Organic Search: $0.10-0.30</li>
          <li>Social Media: $0.50-2.00</li>
          <li>PPC Advertising: $1.00-3.00</li>
          <li>Display Ads: $0.50-1.50</li>
          <li>Email Marketing: $0.05-0.20</li>
          <li>Content Marketing: $0.15-0.50</li>
          <li>Referral Traffic: $0.30-1.00</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Cost Optimization Strategies</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Optimize SEO for organic traffic</li>
          <li>Improve content marketing</li>
          <li>Refine paid campaigns</li>
          <li>Leverage social media</li>
          <li>Implement referral programs</li>
          <li>Focus on retargeting</li>
          <li>Improve website speed</li>
          <li>Optimize user experience</li>
          <li>Analyze channel performance</li>
          <li>Test different ad formats</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Traffic Quality Factors</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Bounce Rate Impact</li>
          <li>Time on Site</li>
          <li>Pages per Visit</li>
          <li>Conversion Rate</li>
          <li>Return Visit Rate</li>
          <li>Engagement Metrics</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">ROI Impact</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>SEO reduces cost per visit by 80%</li>
          <li>Content marketing lowers costs by 60%</li>
          <li>Retargeting improves efficiency by 40%</li>
          <li>Channel optimization saves 25-35%</li>
          <li>A/B testing reduces costs by 15-20%</li>
        </ul>
      </div>
    </div>
  );
};

export default CostPerWebsiteVisitCalculator;
